import { useState, FormEvent, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button, TextButton } from '@safetyhub/ui';
import { useAuthStore } from '../../stores/auth';
import { useToastStore } from '../../stores/toast';
import { verificationApi, extractApiError } from '../../lib/auth';
import PhoneVerification from '../../components/PhoneVerification';

export default function SignupUsername() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { signupWithUsername, isLoading, error, clearError } = useAuthStore();
  const { showToast } = useToastStore();

  // 초대 관련 쿼리 파라미터
  const inviteToken = searchParams.get('inviteToken');
  const prefillPhone = searchParams.get('phone');

  // 폼 데이터
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState(prefillPhone || '');
  const [phoneVerificationToken, setPhoneVerificationToken] = useState('');
  const [email, setEmail] = useState('');
  const [termsAgreed, setTermsAgreed] = useState(false);

  // 상태
  const [localError, setLocalError] = useState('');
  const [usernameAvailable, setUsernameAvailable] = useState<boolean | null>(null);
  const [usernameChecking, setUsernameChecking] = useState(false);

  // 초대 전화번호로 미리 채우기
  useEffect(() => {
    if (prefillPhone && !phone) {
      setPhone(prefillPhone);
    }
  }, [prefillPhone]);

  // 아이디 유효성 검사 (영문소문자 + 숫자 + 언더스코어, 4~20자)
  const isValidUsername = (value: string) => {
    return /^[a-z0-9_]{4,20}$/.test(value);
  };

  // 아이디 중복 확인
  const checkUsername = async () => {
    if (!isValidUsername(username)) return;

    setUsernameChecking(true);
    try {
      const result = await verificationApi.checkUsername(username);
      setUsernameAvailable(result.available);
      if (!result.available) {
        setLocalError('이미 사용 중인 아이디입니다.');
      } else {
        setLocalError('');
      }
    } catch (err) {
      const apiError = extractApiError(err);
      setLocalError(apiError.message);
    } finally {
      setUsernameChecking(false);
    }
  };

  // 비밀번호 유효성 검사 (서버와 동일한 규칙)
  const isValidPassword = (value: string) => {
    return value.length >= 8 && /^(?=.*[a-zA-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?])/.test(value);
  };

  // 휴대폰 인증 완료 핸들러
  const handlePhoneVerified = (token: string) => {
    setPhoneVerificationToken(token);
  };

  // 폼 유효성 검사
  const validateForm = (): boolean => {
    setLocalError('');
    clearError();

    if (!username.trim()) {
      setLocalError('아이디를 입력해주세요.');
      return false;
    }
    if (!isValidUsername(username)) {
      setLocalError('아이디는 영문 소문자, 숫자, 언더스코어(_) 조합 4~20자입니다.');
      return false;
    }
    if (usernameAvailable === false) {
      setLocalError('이미 사용 중인 아이디입니다.');
      return false;
    }
    if (usernameAvailable === null) {
      setLocalError('아이디 중복 확인을 해주세요.');
      return false;
    }
    if (!password) {
      setLocalError('비밀번호를 입력해주세요.');
      return false;
    }
    if (!isValidPassword(password)) {
      setLocalError('비밀번호는 8자 이상, 영문/숫자/특수문자를 포함해야 합니다.');
      return false;
    }
    if (password !== confirmPassword) {
      setLocalError('비밀번호가 일치하지 않습니다.');
      return false;
    }
    if (!name.trim() || name.trim().length < 2) {
      setLocalError('이름은 2자 이상 입력해주세요.');
      return false;
    }
    if (!phoneVerificationToken) {
      setLocalError('휴대폰 인증을 완료해주세요.');
      return false;
    }
    if (!termsAgreed) {
      setLocalError('이용약관에 동의해주세요.');
      return false;
    }

    return true;
  };

  // 회원가입 완료
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      await signupWithUsername({
        username: username.trim(),
        password,
        name: name.trim(),
        phone,
        phoneVerificationToken,
        email: email.trim() || undefined,
        inviteToken: inviteToken || undefined,
      });
      showToast('회원가입이 완료되었습니다. SafetyHub에 오신 것을 환영합니다!', 'success');
      navigate('/', { replace: true });
    } catch (err) {
      // 복구 가능한 탈퇴 계정이 있는 경우 로그인 페이지로 안내
      const apiError = extractApiError(err);
      if (apiError.code === 'AUTH_SIGNUP_ACCOUNT_RECOVERABLE') {
        showToast(apiError.message, 'info');
        navigate('/login');
      }
      // 다른 에러는 store에서 처리
    }
  };

  const displayError = localError || error;
  const isPhoneVerified = !!phoneVerificationToken;

  return (
    <div className="min-h-screen flex items-center justify-center bg-ink-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* 헤더 */}
        <div className="text-center">
          <Link to="/" className="inline-block">
            <h1 className="text-3xl font-bold text-brand-600 hover:text-brand-700">SafetyHub</h1>
          </Link>
          <h2 className="mt-4 text-2xl font-bold text-ink-900">아이디로 가입</h2>
          <p className="mt-2 text-sm text-ink-600">
            <Link to="/signup" className="font-medium text-brand-600 hover:text-brand-500">
              &larr; 다른 방법으로 가입
            </Link>
          </p>
        </div>

        {/* 초대 알림 */}
        {inviteToken && (
          <div className="bg-green-50 border border-green-200 rounded-card p-4">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-green-800">조직 초대가 있습니다</p>
                <p className="mt-1 text-sm text-green-700">
                  회원가입 완료 시 자동으로 초대가 수락됩니다.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* 에러 메시지 */}
        {displayError && (
          <div className="rounded-card bg-red-50 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-700">{displayError}</p>
              </div>
            </div>
          </div>
        )}

        {/* 가입 폼 */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* 아이디 */}
          <div>
            <label htmlFor="username" className="block text-sm font-medium text-ink-700">
              아이디 <span className="text-red-500">*</span>
            </label>
            <div className="mt-1 flex space-x-2">
              <input
                id="username"
                type="text"
                value={username}
                onChange={(e) => {
                  setUsername(e.target.value.toLowerCase());
                  setUsernameAvailable(null);
                }}
                className={`flex-1 px-3 py-2 border rounded-control focus:ring-brand-500 focus:border-brand-500 ${
                  usernameAvailable === true
                    ? 'border-green-300'
                    : usernameAvailable === false
                    ? 'border-red-300'
                    : 'border-ink-300'
                }`}
                placeholder="영문 소문자 + 숫자, 4~20자"
              />
              <Button
                type="button"
                onClick={checkUsername}
                disabled={!isValidUsername(username) || usernameChecking}
                className="whitespace-nowrap"
              >
                {usernameChecking ? '확인 중...' : '중복확인'}
              </Button>
            </div>
            {usernameAvailable === true && (
              <p className="mt-1 text-sm text-green-600">사용 가능한 아이디입니다.</p>
            )}
            <p className="mt-1 text-xs text-ink-500">
              영문 소문자, 숫자, 언더스코어(_) 사용, 4~20자
            </p>
          </div>

          {/* 비밀번호 */}
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-ink-700">
              비밀번호 <span className="text-red-500">*</span>
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1 w-full px-3 py-2 border border-ink-300 rounded-control focus:ring-brand-500 focus:border-brand-500"
              placeholder="8자 이상, 영문/숫자/특수문자"
            />
            <p className="mt-1 text-xs text-ink-500">
              8자 이상, 영문/숫자/특수문자 포함
            </p>
          </div>

          {/* 비밀번호 확인 */}
          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-ink-700">
              비밀번호 확인 <span className="text-red-500">*</span>
            </label>
            <input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="mt-1 w-full px-3 py-2 border border-ink-300 rounded-control focus:ring-brand-500 focus:border-brand-500"
              placeholder="비밀번호 재입력"
            />
          </div>

          {/* 이름 */}
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-ink-700">
              이름 <span className="text-red-500">*</span>
            </label>
            <input
              id="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-1 w-full px-3 py-2 border border-ink-300 rounded-control focus:ring-brand-500 focus:border-brand-500"
              placeholder="홍길동"
            />
          </div>

          {/* 휴대폰 인증 */}
          <div className="pt-2 border-t border-ink-200">
            <PhoneVerification
              phone={phone}
              onPhoneChange={setPhone}
              onVerified={handlePhoneVerified}
              readOnly={!!prefillPhone}
              skipDuplicateCheck
            />
          </div>

          {/* 이메일 (선택) */}
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-ink-700">
              이메일 <span className="text-ink-400">(선택)</span>
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1 w-full px-3 py-2 border border-ink-300 rounded-control focus:ring-brand-500 focus:border-brand-500"
              placeholder="example@company.com"
            />
            <p className="mt-1 text-xs text-ink-500">
              이메일을 등록하면 비밀번호 찾기 등에 활용할 수 있습니다.
            </p>
          </div>

          {/* 이용약관 동의 */}
          <div className="flex items-start">
            <input
              id="terms"
              name="terms"
              type="checkbox"
              checked={termsAgreed}
              onChange={(e) => setTermsAgreed(e.target.checked)}
              className="h-4 w-4 text-brand-600 focus:ring-brand-500 border-ink-300 rounded mt-0.5"
            />
            <label htmlFor="terms" className="ml-2 block text-sm text-ink-700">
              <span>
                {/* TODO: 문서 페이지 추가 후 Link로 복원 */}
                <TextButton
                  type="button"
                  className="underline-offset-2"
                  onClick={() => setTermsAgreed(!termsAgreed)}
                >
                  이용약관
                </TextButton>
                {' 및 '}
                <TextButton
                  type="button"
                  className="underline-offset-2"
                  onClick={() => setTermsAgreed(!termsAgreed)}
                >
                  개인정보처리방침
                </TextButton>
                에 동의합니다.
              </span>
            </label>
          </div>

          {/* 가입 버튼 */}
          <Button type="submit" variant="primary" disabled={isLoading || !isPhoneVerified} className="w-full">
            {isLoading ? '가입 중...' : !isPhoneVerified ? '휴대폰 인증을 완료해주세요' : '회원가입'}
          </Button>
        </form>

        {/* 로그인 링크 */}
        <p className="text-center text-sm text-ink-600">
          이미 계정이 있으신가요?{' '}
          <Link to="/login" className="font-medium text-brand-600 hover:text-brand-500">
            로그인
          </Link>
        </p>

        {/* 푸터 */}
        <p className="mt-4 text-center text-xs text-ink-500">
          SafetyHub &copy; 2025. All rights reserved.
        </p>
      </div>
    </div>
  );
}
